import { CellularConnectivityQuality } from '@/api/connectivity/ConnectivityApi';
import { entityTypes, ImpedimentType } from '@/enums/generic';
import { generateUniqueId } from '@/helpers/uuidUtils';
import { AccessoryType, deviceTypeDetails, deviceTypes } from '@/enums/device';
import {
  CaptureEvent,
  DeviceEvent,
  DeviceEventParams,
  EventType,
  MountEvent,
  UnmountEvent,
} from '@/models/installationPoint/DeviceEvent';

import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { DeviceClasses } from '@/models/devices/Device';
import { PlantDataClasses } from '@/models/plant/Plant';
import { instanceToInstance, Transform, Type } from 'class-transformer';
import {
  Accessory,
  Adapter,
  BatteryPack,
} from '@/models/installationPoint/Accessory';
import { truncateTimestampToSeconds } from '@/helpers/truncateTimeStampToSeconds';
import { TKDService } from '@/models/installationPoint/TKDService';
import { InstallationLocationType } from '@/enums/installationPoint';

export type DeviceType = (typeof deviceTypes)[keyof typeof deviceTypes];

export class Dmgw6LteCheckData {
  rssi?: number;
  quality?: CellularConnectivityQuality;
  timestamp?: string;
}

export enum ScanSource {
  QTOOL = 'QTOOL',
  QTOOL_GATEWAY = 'QTOOL_GATEWAY',
  DITTO = 'DITTO',
}

export enum ScanState {
  NO_SCAN = 'NO_SCAN',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  CANCELLED = 'CANCELLED',
  FINISHED = 'FINISHED',
}

export type DMGScannedDevice = {
  source: ScanSource;
  timestamp?: string; // endedAt
  rssi?: number;
  lqi?: number;
  deviceType?: string;
};

export class GatewayStatus {
  @Type(() => Dmgw6LteCheckData)
  connectionStatus?: Dmgw6LteCheckData;

  status?: ScanState;

  @Transform(
    ({ value }) => (value ? new Map(Object.entries(value)) : undefined),
    { toClassOnly: true }
  )
  devices?: Map<string, DMGScannedDevice[]>;

  toJSON() {
    return {
      connectionStatus: this.connectionStatus,
      status: this.status,
      devices: this.devices ? Object.fromEntries(this.devices) : undefined,
    };
  }
}

export type InstallationPointParams = {
  roomId: string;
  radiatorId?: string;
  plantId?: string;
  supportedDeviceType?: DeviceType;
  accessories: Accessory[];
  activeDeviceId?: string;
  billedInRoomGroupId?: string;
  advancedRegistrationById?: string;
  isAdvancedRegistrationMeter?: boolean;
  isCentralAdvancedRegistrationMeter?: boolean;
  advancedRegistrationMeterType?: string;
  deviceEvents?: DeviceEvent[];
  services?: TKDService[];
  failedInstallationAttempt?: FailedInstallationAttempt;
  ordinal?: number;
  note?: string;
  locationType?: InstallationLocationType;
};

export class InstallationPoint {
  id: string;
  type = entityTypes.installationPoint;
  roomId: string;
  radiatorId?: string;
  plantId?: string;
  supportedDeviceType?: DeviceType;
  activeDeviceId?: string;
  billedInRoomGroupId?: string;
  @Type(() => Accessory, {
    keepDiscriminatorProperty: true,
    discriminator: {
      property: 'type',
      subTypes: [
        { value: Adapter, name: AccessoryType.SRT_ADAPTER },
        { value: Adapter, name: AccessoryType.GATEWAY_HOUSING },
        { value: BatteryPack, name: AccessoryType.SRT_BATTERY_PACK },
      ],
    },
  })
  accessories: Accessory[];
  @Type(() => TKDService)
  services: TKDService[];
  advancedRegistrationById?: string;
  isAdvancedRegistrationMeter = false;
  isCentralAdvancedRegistrationMeter = false;
  advancedRegistrationMeterType = undefined;
  deviceEvents: DeviceEvent[];
  failedInstallationAttempt?: FailedInstallationAttempt;
  failedInstallationAttempts: FailedInstallationAttempt[];
  ordinal = 0;
  note?: string;
  locationType?: InstallationLocationType;
  @Type(() => GatewayStatus)
  gatewayStatus?: GatewayStatus;

  constructor(roomId: string) {
    this.id = generateUniqueId();
    this.roomId = roomId;
    this.radiatorId = undefined;
    this.plantId = undefined;
    this.supportedDeviceType = undefined;
    this.activeDeviceId = undefined;
    this.accessories = [];
    this.services = [];
    this.deviceEvents = [];
    this.failedInstallationAttempt = undefined;
    this.failedInstallationAttempts = [];
    this.ordinal = 0;
    this.note = undefined;
    this.locationType = undefined;
    this.billedInRoomGroupId = undefined;
    this.advancedRegistrationById = undefined;
    this.isAdvancedRegistrationMeter = false;
    this.isCentralAdvancedRegistrationMeter = false;
    this.advancedRegistrationMeterType = undefined;
    this.gatewayStatus = undefined;
  }

  updateProperties(formData: InstallationPoint | InstallationPointParams) {
    const wrongProperties = Object.keys(formData).filter(
      (key) => !Object.keys(this).includes(key)
    );

    if (wrongProperties.length > 0) {
      // throw new Error(
      //   `Provided wrong properties: ${wrongProperties} with ${JSON.stringify(
      //     formData
      //   )}`
      // );
    }
    Object.assign(this, instanceToInstance(formData));
  }

  getParentId(): string {
    return this.roomId;
  }

  getDeviceType() {
    return deviceTypeDetails[this.supportedDeviceType as DeviceType];
  }

  getTagColor() {
    return this.getDeviceType().color;
  }

  getActiveDevice(): DeviceClasses | PlantDataClasses | undefined {
    if (!this.activeDeviceId) return undefined;
    return useEntitiesStore().getEntityById(this.activeDeviceId);
  }

  getActiveDeviceEvent(): DeviceEvent | undefined {
    if (!this.activeDeviceId) return undefined;
    return this.deviceEvents.find(
      (deviceEvent) => deviceEvent.deviceId === this.activeDeviceId
    );
  }

  getLatestUnmountedDevice() {
    const latestUnmountEvent = this.getLatestUnmountEvent();
    if (!latestUnmountEvent) {
      return undefined;
    }
    return useEntitiesStore().getEntityById(latestUnmountEvent?.deviceId);
  }

  getLatestUnmountEvent() {
    const sortedEvents = this.sortEventsByTimestamp();
    return sortedEvents.find((event) => event.type === EventType.UNMOUNT_EVENT);
  }

  sortEventsByTimestamp() {
    return this.deviceEvents.slice().sort((a, b) => {
      const timeA = truncateTimestampToSeconds(a.timestamp);
      const timeB = truncateTimestampToSeconds(b.timestamp);

      if (timeB !== timeA) {
        return timeB - timeA;
      }

      const typeOrder = {
        [EventType.MOUNT_EVENT]: 1,
        [EventType.UNMOUNT_EVENT]: 2,
        [EventType.CAPTURE_EVENT]: 3,
      };

      return typeOrder[a.type] - typeOrder[b.type];
    });
  }

  isDeviceUnmounted(): boolean {
    const latestEvent = this.sortEventsByTimestamp()[0];
    if (!latestEvent) return false;

    const failedTimestamp = this.failedInstallationAttempt?.timestamp;
    const isFailedMoreRecent = failedTimestamp
      ? new Date(failedTimestamp) > new Date(latestEvent.timestamp)
      : false;

    return latestEvent.type === EventType.UNMOUNT_EVENT && !isFailedMoreRecent;
  }

  setActiveDeviceId(deviceId?: string) {
    this.activeDeviceId = deviceId;
    return this;
  }

  setRadiatorId(radiatorId?: string) {
    this.radiatorId = radiatorId;
    return this;
  }

  setPlantId(plantId?: string) {
    this.plantId = plantId;
    return this;
  }

  setSupportedDeviceType(deviceType: DeviceType) {
    this.supportedDeviceType = deviceType;
    return this;
  }

  addFailedInstallationAttempt(
    failedInstallationAttempt: FailedInstallationAttempt,
    supportedDeviceType: DeviceType
  ): this {
    this.unsetActiveDevice();

    if (this.failedInstallationAttempt) {
      this.failedInstallationAttempt.reason = failedInstallationAttempt.reason;
      this.failedInstallationAttempt.note = failedInstallationAttempt.note;
      this.failedInstallationAttempt.type = failedInstallationAttempt.type;
      return this;
    }
    this.failedInstallationAttempt = new FailedInstallationAttempt();
    this.failedInstallationAttempt.id = failedInstallationAttempt.id;
    this.failedInstallationAttempt.note = failedInstallationAttempt.note;
    this.failedInstallationAttempt.reason = failedInstallationAttempt.reason;
    this.failedInstallationAttempt.type = failedInstallationAttempt.type;
    this.failedInstallationAttempts.push(this.failedInstallationAttempt);
    this.supportedDeviceType = supportedDeviceType;
    return this;
  }

  removeFailedInstallationAttempt(): this {
    this.failedInstallationAttempt = undefined;
    const activeDeviceEvent = this.deviceEvents.find(
      (event) => event instanceof MountEvent
    );
    if (activeDeviceEvent) {
      this.activeDeviceId = activeDeviceEvent.deviceId;
    }
    return this;
  }

  mountDevice(
    deviceId: string,
    timestamp: string,
    counter?: number,
    controlValue?: number
  ) {
    if (!deviceId) {
      throw new Error('DeviceClasses has no deviceId and it cannot be mounted');
    }

    const existingMountEvent = this.deviceEvents.find(
      (event) => event instanceof MountEvent && event.deviceId === deviceId
    );
    if (existingMountEvent) {
      existingMountEvent.deviceId = deviceId;
      existingMountEvent.timestamp = timestamp;
      existingMountEvent.counter = counter;
      return existingMountEvent;
    }

    const mountEvent = new MountEvent(
      deviceId,
      timestamp,
      counter,
      controlValue
    );
    this.deviceEvents.push(mountEvent);
    this.setActiveDeviceId(deviceId);
    return mountEvent;
  }

  unmountDevice(
    deviceId: string,
    timestamp: string,
    counter?: number,
    controlValue?: number,
    reason?: string
  ) {
    if (!deviceId) {
      throw new Error(
        'DeviceClasses has no deviceId and it cannot be unmounted'
      );
    }

    const existingUnmountEvent: UnmountEvent | undefined =
      this.deviceEvents.find(
        (event) =>
          event.type === EventType.UNMOUNT_EVENT && event.deviceId === deviceId
      );
    if (existingUnmountEvent) {
      existingUnmountEvent.deviceId = deviceId;
      existingUnmountEvent.timestamp = timestamp;
      existingUnmountEvent.counter = counter;
      existingUnmountEvent.reason = reason;
      if (controlValue) {
        existingUnmountEvent.controlValue = controlValue;
      }
      return existingUnmountEvent;
    }

    const unmountEvent = new UnmountEvent(
      deviceId,
      timestamp,
      counter,
      controlValue,
      reason
    );
    this.deviceEvents.push(unmountEvent);
    this.setActiveDeviceId(undefined);
    return unmountEvent;
  }

  captureDevice(
    deviceId: string,
    timestamp: string,
    counter?: number,
    controlValue?: number
  ) {
    if (!deviceId) {
      throw new Error(
        'DeviceClasses has no deviceId and it cannot be unmounted'
      );
    }

    const existingCaptureEvent: CaptureEvent | undefined =
      this.deviceEvents.find(
        (event) => event instanceof CaptureEvent && event.deviceId === deviceId
      );
    if (existingCaptureEvent) {
      existingCaptureEvent.deviceId = deviceId;
      existingCaptureEvent.timestamp = timestamp;
      existingCaptureEvent.counter = counter;
      if (controlValue) {
        existingCaptureEvent.controlValue = controlValue;
      }
      return;
    }

    this.deviceEvents.push(
      new CaptureEvent(deviceId, timestamp, counter, controlValue)
    );
    this.setActiveDeviceId(deviceId);
  }

  replaceDevice(
    device: DeviceClasses,
    deviceEvent: DeviceEvent,
    oldDevice: DeviceClasses,
    oldDeviceEvent: DeviceEventParams
  ) {
    const eventDate = new Date(oldDeviceEvent?.timestamp);
    const oneSecondBefore = new Date(eventDate.getTime() - 1000);
    this.unmountDevice(
      oldDevice?.id,
      oneSecondBefore.toISOString(),
      oldDeviceEvent?.counter,
      oldDeviceEvent?.controlValue,
      oldDeviceEvent?.reason
    );
    if (this.failedInstallationAttempt === undefined) {
      this.mountDevice(
        device?.id,
        deviceEvent?.timestamp,
        deviceEvent?.counter
      );
    }
  }

  unsetActiveDevice() {
    this.activeDeviceId = undefined;
    this.deviceEvents = [];
  }

  recaptureDevice(device: DeviceClasses, counterOnCapture: number) {
    const timestamp = new Date().toISOString();
    this.captureDevice(device?.id, timestamp, counterOnCapture);
  }

  demountAccessory(oldAccessoryId: string) {
    const existingOldAccessory = this.accessories.find(
      (accessory) => accessory.id === oldAccessoryId
    );
    if (existingOldAccessory) {
      existingOldAccessory.unmountedAt = new Date().toISOString();
    }
  }

  getCurrentBatteryPack() {
    return this.accessories.find(
      (accessory: Accessory) =>
        accessory.type === AccessoryType.SRT_BATTERY_PACK &&
        !accessory.unmountedAt
    ) as BatteryPack | undefined;
  }

  exchangeBatteryPack(newBatteryPack: BatteryPack) {
    const currentBatteryPack = this.accessories.find(
      (accessory: Accessory) =>
        accessory.type === AccessoryType.SRT_BATTERY_PACK &&
        !accessory.unmountedAt
    );
    if (currentBatteryPack) {
      currentBatteryPack.unmountedAt = new Date().toISOString();
    }

    const batteryPack = this.accessories.find(
      (accessory: Accessory) =>
        accessory.id === newBatteryPack.id &&
        accessory.type === AccessoryType.SRT_BATTERY_PACK
    );
    if (batteryPack instanceof BatteryPack) {
      batteryPack.serialNumber = newBatteryPack.serialNumber;
      batteryPack.customizedNumber = newBatteryPack.customizedNumber;
      return;
    }

    this.accessories.push(newBatteryPack);
  }
}

export class FailedInstallationAttempt {
  id: string;
  timestamp: string;
  reason: string;
  note: string;
  type?: ImpedimentType;

  constructor() {
    this.id = generateUniqueId();
    this.timestamp = new Date().toISOString();
    this.reason = '';
    this.note = '';
    this.type = undefined;
  }
}
